import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { MdPayment, MdReceipt, MdCreditCard, MdAutoMode } from 'react-icons/md';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import DataTable from 'react-data-table-component';
import {
    fetchBills,
    fetchInvoices,
    toggleAutoPayment,
    fetchBillingSettings,
    updateBillingSettings,
    createBillingSetupIntent,
    attachBillingPaymentMethod,
} from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';
import LoadingMessage from '../../../shared/layout/feedback/LoadingMessage';
import {
    StyledInput,
    StyledLabel,
    FormGroup,
} from '../../../shared/layout/forms/FormElements';
import EmptyState from '../../../shared/layout/feedback/EmptyState';
import { useCurrency } from '../../../context/CurrencyProvider';

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = publishableKey ? loadStripe(publishableKey) : null;

const BillingSettings = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [bills, setBills] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [autoPaymentEnabled, setAutoPaymentEnabled] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState({ cardBrand: '', cardLastFour: '' });
    const [billingEmail, setBillingEmail] = useState('');
    const [billingProfile, setBillingProfile] = useState(null);
    const [clientSecret, setClientSecret] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('payment');

    useEffect(() => {
        const loadData = async () => {
            try {
                const [billsData, invoicesData, billingSettings] = await Promise.all([
                    fetchBills(),
                    fetchInvoices(),
                    fetchBillingSettings(),
                ]);
                setBills(billsData);
                setInvoices(invoicesData);
                setBillingProfile(billingSettings?.organization_billing || null);
                setPaymentMethod({
                    cardBrand: billingSettings?.card_brand || billingSettings?.organization_billing?.card_brand || '',
                    cardLastFour: billingSettings?.card_last_four || billingSettings?.organization_billing?.card_last_four || '',
                });
                setAutoPaymentEnabled(billingSettings?.auto_payment_enabled ?? false);
                setBillingEmail(billingSettings?.billing_email || '');
                setLoading(false);
            } catch (err) {
                toast.error(t('Settings.Billing.error') + ': ' + err.message);
                setLoading(false);
            }
        };
        loadData();
    }, [t]);

    const handleToggleAutoPayment = async () => {
        try {
            const updated = await toggleAutoPayment(!autoPaymentEnabled);
            setAutoPaymentEnabled(updated?.auto_payment_enabled ?? !autoPaymentEnabled);
            toast.success(t('Settings.Billing.autoPaymentSuccess'));
        } catch (err) {
            toast.error(t('Settings.Billing.autoPaymentError') + ': ' + err.message);
        }
    };

    const handleBillingEmailSave = async () => {
        try {
            const updated = await updateBillingSettings({ billing_email: billingEmail });
            setBillingEmail(updated?.billing_email || '');
            toast.success(t('Settings.Billing.billingEmailSaved'));
        } catch (err) {
            toast.error(t('Settings.Billing.billingEmailError') + ': ' + err.message);
        }
    };

    useEffect(() => {
        const loadSetupIntent = async () => {
            if (activeSection !== 'payment' || clientSecret || !stripePromise) return;
            try {
                const data = await createBillingSetupIntent();
                setClientSecret(data?.client_secret || '');
            } catch (err) {
                toast.error(t('Settings.Billing.setupIntentError') + ': ' + err.message);
            }
        };
        loadSetupIntent();
    }, [activeSection, clientSecret, t]);

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '48px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                fontSize: '14px',
                color: '#111827',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb',
                    cursor: 'default',
                },
            },
        },
    };

    const billsColumns = [
        {
            name: t('Settings.Billing.period'),
            selector: row => `${new Date(row.billing_period_start).toLocaleDateString()} - ${new Date(row.billing_period_end).toLocaleDateString()}`,
            sortable: true,
        },
        {
            name: t('Settings.Billing.amount'),
            selector: row => formatResolvedAmount({
                eurAmount: row.total_amount,
                localAmount: row.total_amount_local ?? row.total_price_seller_local,
                localCurrencyId: row.currency_id ?? row.seller_currency_id,
            }),
            sortable: true,
        },
        {
            name: t('Settings.Billing.status'),
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <StatusBadge $status={row.status}>
                    {row.status}
                </StatusBadge>
            )
        }
    ];

    const invoicesColumns = [
        {
            name: t('Settings.Billing.invoiceNumber'),
            selector: row => row.invoice_number,
            sortable: true,
        },
        {
            name: t('Settings.Billing.issuedDate'),
            selector: row => new Date(row.issued_at).toLocaleDateString(),
            sortable: true,
        },
        {
            name: t('Settings.Billing.amount'),
            selector: row => formatResolvedAmount({
                eurAmount: row.total_amount,
                localAmount: row.total_amount_local,
                localCurrencyId: row.currency_id,
            }),
            sortable: true,
        },
        {
            name: t('Settings.Billing.download'),
            cell: row => (
                <a href={row.pdf_url} target="_blank" rel="noopener noreferrer" style={{ color: '#701a82', textDecoration: 'none', fontWeight: 500 }}>
                    {t('Settings.Billing.download')}
                </a>
            ),
        }
    ];

    if (loading) return <LoadingMessage>{t('Settings.loading')}</LoadingMessage>;

    return (
        <Container>
            <SectionLayout>
                <SectionTabs>
                    <SectionTab $active={activeSection === 'payment'} onClick={() => setActiveSection('payment')}>
                        <MdCreditCard size={18} />
                        {t('Settings.Billing.paymentMethod')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'auto'} onClick={() => setActiveSection('auto')}>
                        <MdAutoMode size={18} />
                        {t('Settings.Billing.autoPayment')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'bills'} onClick={() => setActiveSection('bills')}>
                        <MdReceipt size={18} />
                        {t('Settings.Billing.monthlyBills')}
                    </SectionTab>
                    <SectionTab $active={activeSection === 'invoices'} onClick={() => setActiveSection('invoices')}>
                        <MdPayment size={18} />
                        {t('Settings.Billing.invoices')}
                    </SectionTab>
                </SectionTabs>

                <SectionContent>
                    {activeSection === 'payment' && (
                        <StyledCard title={t('Settings.Billing.paymentMethod')}>
                            <Form>
                                <FormGroup>
                                    <StyledLabel>{t('Settings.Billing.currentPaymentMethod')}</StyledLabel>
                                    <InfoText>
                                        {paymentMethod.cardBrand
                                            ? `${paymentMethod.cardBrand.toUpperCase()} •••• ${paymentMethod.cardLastFour}`
                                            : t('Settings.Billing.noPaymentMethod')}
                                    </InfoText>
                                </FormGroup>
                                <FormGroup>
                                    <StyledLabel>{t('Settings.Billing.updatePaymentMethod')}</StyledLabel>
                                    <InfoText style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>
                                        {t('Settings.Billing.enterCard')}
                                    </InfoText>
                                    {stripePromise ? (
                                        <CardContainer>
                                            {clientSecret ? (
                                                <Elements stripe={stripePromise}>
                                                    <BillingCardForm
                                                        clientSecret={clientSecret}
                                                        onSaved={(profile) => {
                                                            setBillingProfile(profile);
                                                            setPaymentMethod({
                                                                cardBrand: profile?.card_brand || '',
                                                                cardLastFour: profile?.card_last_four || '',
                                                            });
                                                        }}
                                                    />
                                                </Elements>
                                            ) : (
                                                <InfoText>{t('Settings.Billing.loadingPaymentForm')}</InfoText>
                                            )}
                                        </CardContainer>
                                    ) : (
                                        <InfoText>{t('Settings.Billing.missingPublishableKey')}</InfoText>
                                    )}
                                </FormGroup>
                                <FormGroup>
                                    <StyledLabel htmlFor="billing_email">{t('Settings.Billing.billingEmail')}</StyledLabel>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <StyledInput
                                            id="billing_email"
                                            type="email"
                                            value={billingEmail}
                                            onChange={(event) => setBillingEmail(event.target.value)}
                                            placeholder={t('Settings.Billing.billingEmailPlaceholder')}
                                            aria-label={t('Settings.Billing.billingEmail')}
                                        />
                                        <MainButton type="button" onClick={handleBillingEmailSave} style={{ whiteSpace: 'nowrap' }}>
                                            {t('Settings.Billing.saveBillingEmail')}
                                        </MainButton>
                                    </div>
                                </FormGroup>
                            </Form>
                        </StyledCard>
                    )}

                    {activeSection === 'auto' && (
                        <StyledCard title={t('Settings.Billing.autoPayment')}>
                            <Form>
                                <FormGroup>
                                    <StyledLabel style={{ flexDirection: 'row', alignItems: 'center', gap: '0.75rem', display: 'flex' }}>
                                        <ToggleSwitch
                                            type="checkbox"
                                            checked={autoPaymentEnabled}
                                            onChange={handleToggleAutoPayment}
                                            aria-label={t('Settings.Billing.enableAutoPayment')}
                                        />
                                        <span>{t('Settings.Billing.enableAutoPayment')}</span>
                                    </StyledLabel>
                                </FormGroup>
                            </Form>
                        </StyledCard>
                    )}

                    {activeSection === 'bills' && (
                        <StyledCard title={t('Settings.Billing.monthlyBills')} noPadding>
                            <DataTable
                                columns={billsColumns}
                                data={bills}
                                customStyles={customStyles}
                                pagination
                                noDataComponent={<EmptyState>{t('Settings.Billing.noBills')}</EmptyState>}
                            />
                        </StyledCard>
                    )}

                    {activeSection === 'invoices' && (
                        <StyledCard title={t('Settings.Billing.invoices')} noPadding>
                            <DataTable
                                columns={invoicesColumns}
                                data={invoices}
                                customStyles={customStyles}
                                pagination
                                noDataComponent={<EmptyState>{t('Settings.Billing.noInvoices')}</EmptyState>}
                            />
                        </StyledCard>
                    )}
                </SectionContent>
            </SectionLayout>
        </Container>
    );
};

// Styled Components
const Container = styled.div`
    width: 100%;
`;

const SectionLayout = styled.div`
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 1.5rem;
    align-items: start;

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }
`;

const SectionTabs = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const SectionTab = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid ${({ $active }) => ($active ? '#701a82' : 'transparent')};
    background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : 'transparent')};
    color: ${({ $active }) => ($active ? '#701a82' : '#6b7280')};
    font-weight: 500;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:hover {
        background: ${({ $active }) => ($active ? 'rgba(112, 26, 130, 0.05)' : '#f9fafb')};
        color: #701a82;
    }
`;

const SectionContent = styled.div`
    flex: 1;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const ToggleSwitch = styled.input`
    appearance: none;
    width: 40px;
    height: 24px;
    background: #e5e7eb;
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    outline: none;
    transition: background 0.2s;

    &:checked {
        background: #701a82;
    }

    &:after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        background: #fff;
        border-radius: 50%;
        transition: transform 0.2s;
    }

    &:checked:after {
        transform: translateX(16px);
    }
`;

const InfoText = styled.p`
    font-size: 0.9375rem;
    color: #111827;
    margin: 0;
`;

const CardContainer = styled.div`
    padding: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #f9fafb;
`;

const CardField = styled.div`
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    background: #fff;
    transition: border-color 0.2s;

    &:hover {
        border-color: #9ca3af;
    }
`;

const CardActions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 1rem;
`;

const CardError = styled.div`
    margin-top: 0.5rem;
    color: #ef4444;
    font-size: 0.875rem;
`;

const StatusBadge = styled.span`
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    background: ${({ $status }) =>
        $status === 'paid' ? '#dcfce7' :
            $status === 'pending' ? '#fef9c3' :
                '#fee2e2'};
    color: ${({ $status }) =>
        $status === 'paid' ? '#166534' :
            $status === 'pending' ? '#854d0e' :
                '#991b1b'};
`;


const BillingCardForm = ({ clientSecret, onSaved }) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async () => {
        if (!stripe || !elements || !clientSecret) return;
        setSaving(true);
        setErrorMessage('');

        const result = await stripe.confirmCardSetup(clientSecret, {
            payment_method: {
                card: elements.getElement(CardElement),
            },
        });

        if (result.error) {
            setErrorMessage(result.error.message || t('Settings.Billing.cardError'));
            setSaving(false);
            return;
        }

        try {
            const profile = await attachBillingPaymentMethod(result.setupIntent.payment_method);
            onSaved(profile);
            toast.success(t('Settings.Billing.cardSaved'));
        } catch (err) {
            setErrorMessage(err.message || t('Settings.Billing.cardError'));
        } finally {
            setSaving(false);
        }
    };

    const cardOptions = useMemo(
        () => ({
            style: {
                base: {
                    fontSize: '15px',
                    color: '#111827',
                    '::placeholder': {
                        color: '#9ca3af',
                    },
                    iconColor: '#701a82',
                },
            },
        }),
        []
    );

    return (
        <>
            <CardField>
                <CardElement options={cardOptions} />
            </CardField>
            {errorMessage && <CardError>{errorMessage}</CardError>}
            <CardActions>
                <MainButton type="button" onClick={handleSubmit} disabled={!stripe || saving}>
                    {saving ? t('Settings.Billing.savingCard') : t('Settings.Billing.saveCard')}
                </MainButton>
            </CardActions>
        </>
    );
};

export default BillingSettings;
